import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { SearchInput } from '@ds';
import { useHubResourcesList } from '@lp/features/RoutePlanner/hooks/useHubResourcesList';
import { addHours } from 'date-fns';
import { FilterDropdown } from '@ui';
import { noop } from 'lodash-es';
import { getFilterTitle } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/components/CaseResourceFilters/utils';
import { ClinicalSpecialistTable } from '@lp/features/AssignmentTables';
import { ZonedDateTime } from '@internationalized/date';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { useGoTimeState } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/hooks';
import {
  GoTimePicker,
  OpenAssignmentCalendarButton,
  OrDateTimePicker,
} from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/components';
import { getTimeZonedDate } from '@/utils/helpers';
import { CSAssignmentCandidate, EmployeeRole } from '@/api/apis/resources';
import { CaseResourceType, OrganType } from '@/api/apis/case';
import { LegLocationTypeEnum, RouteTypeEnum } from '@/api/apis/route';
import { useCaseDetails } from '@/hooks/useCaseDetails';

export type RoutePlannerClinicalSpecialistListProps = {
  organType: OrganType;
  selectedHubId?: string;
  selectedResourceId?: string;
  showHubDropdown?: boolean;
  isSwap?: boolean;
  onAction?: (data: CSAssignmentCandidate, cb: () => void, goDateTime: ZonedDateTime) => void;
  onConfirmation?: (data: CSAssignmentCandidate) => void;
};

export const RoutePlannerClinicalSpecialistList = ({
  organType,
  selectedHubId,
  selectedResourceId,
  onAction,
  onConfirmation,
  showHubDropdown = true,
  isSwap = false,
}: RoutePlannerClinicalSpecialistListProps) => {
  const { legs } = useRouteDetails(RouteTypeEnum.Main);
  const { OCSConsole: devices } = useRouteResources();
  const {
    caseId,
    caseName,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    donorType,
    resources,
    trialProgram,
  } = useCaseDetails();
  const orCaseDateTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  const { applyGoTime, goDateTime, setGoDateTime } = useGoTimeState(orCaseDateTime);

  const defaultHubId = useMemo(() => {
    if (selectedHubId) return selectedHubId;

    const legStartHub = legs.find((s) => s.departureLocation.locationType === LegLocationTypeEnum.hub);

    if (
      legStartHub?.departureLocation.locationType === LegLocationTypeEnum.hub &&
      legStartHub?.departureLocation.locationId
    ) {
      return legStartHub?.departureLocation.id;
    }

    if (devices[0]?.data?.hub?.id) {
      return devices[0]?.data?.hub?.id;
    }

    return '';
  }, [selectedHubId, legs, devices]);

  const [searchValue, setSearchValue] = useState('');
  const { hubSelectList, onHubSelectChange, selectedHub } = useHubResourcesList({
    defaultHubId,
    isClosestHubCalculate: !selectedHubId,
    fetchResources: false,
  });

  const handleReserve = useCallback(
    (data: CSAssignmentCandidate) => {
      onAction?.(data, () => onConfirmation?.(data), goDateTime);
    },
    [onAction, onConfirmation, goDateTime],
  );

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const orDateTime = getTimeZonedDate(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  const orTimeCorrected = orDateTime?.toAbsoluteString() ?? addHours(new Date(), 6).toISOString();
  const assignedResources = resources.filter(
    (r) => r.resourceType === CaseResourceType.ClinicalSpecialist || r.resourceType === CaseResourceType.Surgeon,
  );

  return (
    <div className="flex flex-col place-items-stretch gap-6">
      {showHubDropdown && (
        <div className="flex items-center justify-between gap-6 min-w-[50%]">
          <div className="flex gap-10 items-center">
            {isSwap ? (
              <span className="text-m-m">
                {selectedHub?.id ? `HUB: ${getFilterTitle(hubSelectList, selectedHub.id)}` : 'HUB:'}
              </span>
            ) : (
              <FilterDropdown
                isFaded
                title={selectedHub?.id ? getFilterTitle(hubSelectList, selectedHub.id) : 'NOP Hub'}
                items={hubSelectList}
                selectedKeys={selectedHub?.id ? [selectedHub.id] : []}
                mode="single"
                showCounter={false}
                onSelectedKeysChange={(values) => onHubSelectChange(values[0])}
              />
            )}
            <OrDateTimePicker
              showOrTypeSwitch={false}
              orDateTime={orCaseDateTime!}
              onOrDateTimeChange={noop}
              isCustom={false}
              onIsCustomChange={noop}
            />
            {applyGoTime && <GoTimePicker dateTime={goDateTime} setDateTime={setGoDateTime} />}
          </div>
          <OpenAssignmentCalendarButton
            day={orTimeCorrected}
            employeeRole={EmployeeRole.ClinicalSpecialist}
            hubId={selectedHub?.id}
          />
        </div>
      )}
      <SearchInput placeholder="Search" value={searchValue} onChange={onSearchChange} />
      <div className="flex flex-col">
        <ClinicalSpecialistTable
          searchTerm={searchValue}
          orTime={orTimeCorrected}
          goTime={goDateTime.toAbsoluteString()}
          applyGoTime={applyGoTime}
          caseId={caseId}
          hubId={selectedHub?.id || ''}
          organ={organType}
          donor={donorType}
          assignedResources={assignedResources}
          isLogistics
          selectedSpecialistsIds={selectedResourceId ? [selectedResourceId] : []}
          onReserve={handleReserve}
          isSwap={isSwap}
          caseName={caseName}
          className="h-auto! min-h-[200px] overflow-auto"
          trialProgramId={trialProgram?.id}
        />
      </div>
    </div>
  );
};
